import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import IdeView from './IdleView';
import VoidView from './VoidView';
import { useGetSignedDocumentUrl } from '../../../../hooks/useGetSignedDocumentUrl';
import { useMoveDocumentToFolder } from '../../../../hooks/useMoveDocument';
import { useKnowledgeContext } from '../../../../providers/Knowledge/Knowledge.provider';
import { FileItem, ViewType } from '../../../../types/File/File.types';
import FileGrid from '../Grid/Grid';
import InfiniteScrollIndicator from '../InfiniteScrollIndicator/InfiniteScrollIndicator';
import FileTable from '../Table/Table';

const MainView = () => {
    const { t } = useTranslation();
    const { toast, toasts } = useToast();
    const {
        pages,
        viewType,
        selectedItems,
        isLoadingMorePages,
        fetchNextPage,
        handlePreviewFile,
        hasNextPage,
        isLoadingDocuments,
        isFetchingDocuments,
        isLoadingUpdateDocument,
        isLoadingDelete,
        isErrorLoadingDocuments,
        handleDialogState,
        handleSelectItems,
        handleDragInternal,
        handleSelectAllItems,
        handleSelectItemsToDelete,
        handleSelectItemToRename,
        handleUpdateDocument,
        handleSelectItemToMove,
        handleSelectItemToReplace,
    } = useKnowledgeContext();

    const navigate = useNavigate();
    const { mutateAsync } = useMoveDocumentToFolder();
    const [selectedDownloadFile, setSelectedDownloadFile] = useState<FileItem | null>(null);
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const { data } = useGetSignedDocumentUrl(selectedDownloadFile?.id);

    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadingRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (isLoadingMorePages) return;
            if (observerRef.current) observerRef.current.disconnect();

            observerRef.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });

            if (node) observerRef.current.observe(node);
        },
        [fetchNextPage, hasNextPage, isLoadingMorePages],
    );

    const openRenameDialog = useCallback(
        (item: FileItem) => {
            handleDialogState('isRenameDialogOpen', true);
            handleSelectItemToRename(item);
        },
        [handleDialogState, handleSelectItemToRename],
    );

    const handleToggleActive = useCallback(
        (file: FileItem, active: boolean) => {
            toast({
                title: t('toast.activateDocuments.request.title'),
                description: t('toast.activateDocuments.request.description'),
                variant: 'default',
            });
            handleUpdateDocument({ active }, file.id, file.type);
        },
        [handleUpdateDocument, t, toast],
    );

    const openMoveDialog = useCallback(
        (item: FileItem) => {
            handleDialogState('isMoveDialogOpen', true);
            handleSelectItemToMove(item);
        },
        [handleDialogState, handleSelectItemToMove],
    );

    const openPreviewDialog = useCallback(
        (file: FileItem) => {
            handlePreviewFile(file);
            handleDialogState('isPreviewModalOpen', true);
        },
        [handleDialogState, handlePreviewFile],
    );

    const openDeleteDialog = useCallback(
        (file: FileItem) => {
            handleSelectItemsToDelete([file.id]);
            handleDialogState('isDeleteDialogOpen', true);
        },
        [handleDialogState, handleSelectItemsToDelete],
    );

    const handleDownload = useCallback((file: FileItem) => {
        setSelectedDownloadFile(file);
    }, []);

    useEffect(() => {
        if (data?.signed_url && selectedDownloadFile) {
            fetch(data.signed_url)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.blob();
                })
                .then((blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = selectedDownloadFile.name || 'download';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    setSelectedDownloadFile(null);
                })
                .catch((error) => {
                    console.error('Error downloading file:', error);
                    toast({
                        title: t('knowledgeManager.download.errorTitle'),
                        description: t('knowledgeManager.download.errorDescription'),
                        variant: 'destructive',
                    });
                    setSelectedDownloadFile(null);
                });
        }
    }, [data?.signed_url, selectedDownloadFile, toast, t]);

    const handleFolderNavigation = useCallback(
        (foldeId: string) => {
            navigate(`/knowledge/${foldeId}`);
        },
        [navigate],
    );

    const handleMoveOnDrop = useCallback(
        (fileId: string, folderId: string) => {
            console.log('handleMoveOnDrop', fileId, folderId);
            const payload: {
                documentId: string;
                organizationId: string;
                destinationFolderId?: string;
            } = {
                documentId: fileId,
                organizationId: userInfo?.currentOrganization || '',
            };

            // Only set destinationFolderId if not moving to root
            if (folderId !== userInfo?.currentOrganization) {
                payload.destinationFolderId = folderId;
            }

            mutateAsync(payload)
                .then(async () => {
                    await queryClient.invalidateQueries({ queryKey: ['documents'] });
                    toast({
                        title: t('modals.moveFile.success.title'),
                        description: t('modals.moveFile.success.description'),
                        variant: 'default',
                    });
                })
                .catch((error) => {
                    console.error('Move operation failed:', error);
                    toast({
                        title: t('modals.moveFile.error.title'),
                        description: t('modals.moveFile.error.description'),
                        variant: 'destructive',
                    });
                });
        },
        [mutateAsync, queryClient, t, toast, userInfo?.currentOrganization],
    );

    const handleReplace = useCallback(
        (file: FileItem) => {
            handleSelectItemToReplace(file);
            handleDialogState('isReplaceDialogOpen', true);
        },
        [handleDialogState, handleSelectItemToReplace],
    );

    // Seleccionar vista segun el tipo de vista seleccionada
    const mainViewTypes: Record<ViewType, JSX.Element> = useMemo(
        () => ({
            grid: (
                <FileGrid
                    files={pages}
                    onNavigate={handleFolderNavigation}
                    onMoveFile={handleMoveOnDrop}
                    onRename={openRenameDialog}
                    onDelete={openDeleteDialog}
                    onInternalDragStart={(fileId) => {
                        handleDragInternal(true, fileId);
                    }}
                    onInternalDragEnd={() => {
                        handleDragInternal(false, null);
                    }}
                    draggedFileId={null}
                    onMoveToFolder={openMoveDialog}
                    onToggleActive={handleToggleActive}
                    selectedItems={selectedItems}
                    onSelectItem={handleSelectItems}
                    onPreviewFile={openPreviewDialog}
                    onDownload={handleDownload}
                    onReplace={handleReplace}
                />
            ),
            table: (
                <FileTable
                    files={pages}
                    onNavigate={handleFolderNavigation}
                    onMoveFile={handleMoveOnDrop} // maneja archivos dentro de carpetas
                    onRename={openRenameDialog}
                    onDelete={openDeleteDialog}
                    onInternalDragStart={(fileId) => {
                        handleDragInternal(true, fileId);
                    }}
                    onInternalDragEnd={() => {
                        handleDragInternal(false, null);
                    }}
                    draggedFileId={null}
                    onMoveToFolder={openMoveDialog}
                    onToggleActive={handleToggleActive}
                    selectedItems={selectedItems}
                    onSelectItem={handleSelectItems}
                    onSelectAll={handleSelectAllItems}
                    onPreviewFile={openPreviewDialog}
                    onDownload={handleDownload}
                    userInfo={userInfo}
                    onReplace={handleReplace}
                />
            ),
        }),
        [
            pages,
            handleFolderNavigation,
            handleMoveOnDrop,
            openRenameDialog,
            openDeleteDialog,
            openMoveDialog,
            handleToggleActive,
            selectedItems,
            handleSelectItems,
            openPreviewDialog,
            handleSelectAllItems,
            handleDragInternal,
            userInfo,
            handleReplace,
            handleDownload,
        ],
    );

    // Renderizar la vista seleccionada junto con el indicador de
    // carga si hay paginas disponibles

    const renderMainView = useMemo(() => {
        return pages.length > 0 ? (
            <>
                {(isLoadingUpdateDocument || isLoadingDelete || isFetchingDocuments) && (
                    <div className="w-full h-1 overflow-hidden bg-gray-200">
                        <div className="h-full animate-gradientSlide bg-gradient-to-r from-[#5317ee] via-[#d4c1fb] to-[#5317ee] bg-[length:200%_100%]"></div>
                    </div>
                )}
                {mainViewTypes[viewType as ViewType]}
                {/* Indicador de carga para scroll infinito */}
                {hasNextPage && <InfiniteScrollIndicator ref={loadingRef} isLoading={isLoadingMorePages} />}
            </>
        ) : (
            <VoidView />
        );
    }, [
        hasNextPage,
        isFetchingDocuments,
        isLoadingDelete,
        isLoadingMorePages,
        isLoadingUpdateDocument,
        loadingRef,
        mainViewTypes,
        pages.length,
        viewType,
    ]);

    const isResponseReady = isLoadingDocuments || isErrorLoadingDocuments;
    useEffect(() => {
        if (
            isErrorLoadingDocuments &&
            !toasts.some((toast) => toast.title === t('toast.loadingDocuments.error.title'))
        ) {
            toast({
                title: t('toast.loadingDocuments.error.title'),
                description: t('toast.loadingDocuments.error.description'),
                variant: 'destructive',
            });
        }
    }, [isErrorLoadingDocuments, t, toast, toasts]);

    return isResponseReady ? (
        <IdeView isLoading={isLoadingDocuments} isError={isErrorLoadingDocuments} />
    ) : (
        renderMainView
    );
};

export default MainView;
